import * as React from 'react';
import type { ChangeContext } from '@caliber-2/discovery-core/maps';
import {
  applyCanvasEdit,
  type CanvasSnapshot,
  type ChangeOperation,
  type MapIssue,
  type MapModel,
} from './map-logic';

/**
 * Edit mode on the canvas (T-0189, Decision 54.32), for engagement maps and
 * reference processes alike. Each gesture is read against the map as edited
 * so far and the map is drawn again from the model, laid out by code. The
 * edits are kept as one batch of operations; Done editing sends them as one
 * change set (the API checks a palette add on its own as unconnected, so the
 * batch goes together). Nothing is saved from the canvas itself.
 */
export interface MapEditor {
  editing: boolean;
  /** The map as edited so far (the base when not editing). */
  model: MapModel;
  batch: ChangeOperation[];
  /** What the edited map still needs before it can be saved. */
  issues: MapIssue[];
  /** Bumped to draw the canvas again (a refused gesture is put back). */
  redraw: number;
  start: () => void;
  stop: () => void;
  onCanvasChange: (canvas: CanvasSnapshot) => void;
}

export function useMapEditor(
  base: MapModel,
  context: ChangeContext,
  onNote: (text: string, tone: 'default' | 'critical') => void,
): MapEditor {
  const [editing, setEditing] = React.useState(false);
  const [batch, setBatch] = React.useState<ChangeOperation[]>([]);
  const [model, setModel] = React.useState<MapModel>(base);
  const [issues, setIssues] = React.useState<MapIssue[]>([]);
  const [redraw, setRedraw] = React.useState(0);

  const latest = React.useRef({ base, batch, context, onNote });
  latest.current = { base, batch, context, onNote };

  // A new version underneath (applied, undone, reloaded): start from it.
  React.useEffect(() => {
    if (!editing) setModel(base);
  }, [base, editing]);

  const start = React.useCallback(() => {
    setBatch([]);
    setIssues([]);
    setModel(latest.current.base);
    setEditing(true);
  }, []);

  const stop = React.useCallback(() => {
    setEditing(false);
    setBatch([]);
    setIssues([]);
    setModel(latest.current.base);
  }, []);

  const onCanvasChange = React.useCallback((canvas: CanvasSnapshot) => {
    const { base: from, batch: done, context: ctx, onNote: note } = latest.current;
    const out = applyCanvasEdit(from, done, canvas, ctx);
    if (out.kind === 'edited') {
      latest.current.batch = out.batch;
      setBatch(out.batch);
      setModel(out.model);
      setIssues(out.issues);
      if (out.note) note(out.note, 'default');
      return;
    }
    // Put the canvas back: the drawing always comes from the model.
    setRedraw((n) => n + 1);
    if (out.kind === 'refused') note(out.message, 'critical');
  }, []);

  return {
    editing,
    model: editing ? model : base,
    batch,
    issues,
    redraw,
    start,
    stop,
    onCanvasChange,
  };
}
